import { DisposableStore, toDisposable } from './disposable'
import { Slot } from './slot'

// Builds the PluginContext handed to a plugin's activate(). Every
// registration is tracked so deactivation reliably undoes everything.
export function createPluginContext(manifest, kernel) {
  const pluginId = manifest.id
  const store = new DisposableStore()
  const abort = new AbortController()

  const events = {
    on: (type, handler, opts) => store.add(kernel.events.on(type, handler, opts)),
    // the id lets a person's keymap (omni.shortcuts-help) name this binding
    shortcut: (combo, handler, opts) =>
      store.add(kernel.events.shortcut(combo, handler, { ...opts, id: `${pluginId}:${combo.toLowerCase()}` })),
    listShortcuts: () => kernel.events.listShortcuts(),
    setKeymap: (map) => kernel.events.setKeymap(map),
    onShortcutsChange: (cb) => store.add(kernel.events.onShortcutsChange(cb)),
    releasePointer: (id) => kernel.events.releasePointer(id),
    getActivePointers: () => kernel.events.getActivePointers(),
  }

  const services = {
    provide: (id, impl) => store.add(kernel.services.provide(id, impl)),
    get: (id) => kernel.services.get(id),
    getNow: (id) => kernel.services.getNow(id),
    whenAvailable: (id, cb) => store.add(kernel.services.whenAvailable(id, cb)),
  }

  const ui = {
    addToSlot: (slot, component, opts) =>
      store.add(kernel.slots.addToSlot(slot, component, { ...opts, pluginId })),
    defineSlot: (name) => store.add(kernel.slots.defineSlot(name)),
    // lets a plugin render a slot inside its own components (e.g. the camera
    // plugin renders 'canvas.world' inside its transform layer)
    Slot: ({ name, className }) => (
      <Slot name={name} registry={kernel.slots} className={className} />
    ),
  }

  const storageKey = (key) => `omni.plugin.${pluginId}.${key}`
  const storage = {
    async get(key) {
      try {
        return JSON.parse(localStorage.getItem(storageKey(key)))
      } catch {
        return null
      }
    },
    async set(key, value) {
      localStorage.setItem(storageKey(key), JSON.stringify(value))
    },
  }

  let restarting = false

  const ctx = {
    pluginId,
    manifest, // the plugin's registry manifest (id, version, config, ...)
    events,
    services,
    ui,
    storage,
    log: (...args) => console.log(`[${pluginId}]`, ...args),
    signal: abort.signal,
    // true while a kernel restart stops this plugin and starts it again at once (read it in onDispose)
    get restarting() { return restarting },
    onDispose: (fn) => store.add(toDisposable(fn)),
  }

  return {
    ctx,
    dispose({ restarting: again = false } = {}) {
      restarting = again
      abort.abort()
      store.dispose()
    },
  }
}
